import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AiAssessmentSettings, AiAssessmentSettingsDetail } from '../../models/chat.models';
import { MaterialModule } from '../../material.module';
import { AiSettingsService } from '../../services/ai-settings.service';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';

@Component({
  selector: 'modernization-application-settings',
  templateUrl: './application-settings.component.html',
  styleUrls: ['./application-settings.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, SpinnerComponent]
})
export class ApplicationSettingsComponent implements OnInit {
  @Input() embedded = false;

  loading = false;
  saving = false;
  error: string | null = null;
  successMessage: string | null = null;
  updatedAtUtc: string | null = null;
  private assessmentSettings: AiAssessmentSettingsDetail | null = null;

  readonly form = this.fb.group({
    organization: ['', [Validators.required, Validators.maxLength(200)]],
    project: ['', [Validators.required, Validators.maxLength(200)]],
    workItemTitlePrefix: ['', [Validators.maxLength(200)]],
    parentWorkItemId: [0, [Validators.required, Validators.min(1)]]
  });

  constructor(
    private fb: FormBuilder,
    private aiSettingsService: AiSettingsService
  ) {}

  ngOnInit(): void {
    this.loadSettings();
  }

  loadSettings(): void {
    this.loading = true;
    this.error = null;
    this.successMessage = null;

    this.aiSettingsService.getActiveAssessmentSettings().subscribe({
      next: (settings) => {
        this.assessmentSettings = settings;
        this.form.patchValue({
          organization: settings.azureDevOpsOrganization ?? '',
          project: settings.azureDevOpsProject ?? '',
          workItemTitlePrefix: settings.azureDevOpsWorkItemTitlePrefix ?? '',
          parentWorkItemId: settings.azureDevOpsParentWorkItemId ?? 0
        });
        this.updatedAtUtc = String(settings.updatedAt ?? '');
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading application settings:', err);
        this.error = err?.status === 404
          ? 'No AI assessment settings exist yet. Create the first settings version in AI Config before editing application settings.'
          : 'Failed to load application settings';
        this.loading = false;
      }
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.assessmentSettings?.connection?.id) {
      this.error = 'Create and select a Microsoft Foundry connection in AI Config before saving application settings.';
      return;
    }

    this.saving = true;
    this.error = null;
    this.successMessage = null;

    const raw = this.form.getRawValue();
    const current = this.assessmentSettings;

    const payload: AiAssessmentSettings = {
      id: current.id,
      version: current.version,
      versionName: current.versionName,
      isActive: current.isActive,
      aiConnectionId: current.connection.id,
      keyVaultSecretNameForPat: current.keyVaultSecretNameForPat,
      azureDevOpsBaseUrl: current.azureDevOpsBaseUrl,
      azureDevOpsOrganization: raw.organization ?? '',
      azureDevOpsProject: raw.project ?? '',
      azureDevOpsWorkItemTitlePrefix: raw.workItemTitlePrefix ?? '',
      azureDevOpsParentWorkItemId: Number(raw.parentWorkItemId ?? 0),
      pollingIntervalSeconds: current.pollingIntervalSeconds,
      maxFileSizeBytes: current.maxFileSizeBytes,
      maxContentCharsPerDimensionChunk: current.maxContentCharsPerDimensionChunk,
      cicdPrompt: current.cicdPrompt,
      authPrompt: current.authPrompt,
      codePrompt: current.codePrompt,
      dependenciesPrompt: current.dependenciesPrompt,
      hostingPrompt: current.hostingPrompt,
      apiPrompt: current.apiPrompt,
      containerPrompt: current.containerPrompt,
      testingPrompt: current.testingPrompt,
      productionPrompt: current.productionPrompt,
      databasePrompt: current.databasePrompt,
      masterPrompt: current.masterPrompt,
      additionalDetailsPrompt: current.additionalDetailsPrompt,
      lightweightCodeValidationPrompt: current.lightweightCodeValidationPrompt,
      createdAt: current.createdAt,
      updatedAt: current.updatedAt
    };

    this.aiSettingsService.saveAssessmentSettings(payload).subscribe({
      next: (settings) => {
        this.assessmentSettings = settings;
        this.updatedAtUtc = String(settings.updatedAt ?? '');
        this.successMessage = 'Application settings saved.';
        this.saving = false;
      },
      error: (err) => {
        console.error('Error saving application settings:', err);
        this.error = err?.error?.message || 'Failed to save application settings';
        this.saving = false;
      }
    });
  }
}